import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LogOut, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Seo } from '../components/Seo';
import { LeadStats } from '../components/admin/LeadStats';
import { LeadFilters } from '../components/admin/LeadFilters';
import { LeadsTable } from '../components/admin/LeadsTable';
import { getCurrentUser, getLeads, logout } from '../utils/api';

const localDay = (iso) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const filterLeads = (leads, { type, search, from, to }) => {
  const q = search.trim().toLowerCase();
  return leads.filter((l) => {
    if (type !== 'all' && l.type !== type) return false;
    const day = localDay(l.created_at);
    if (from && day < from) return false;
    if (to && day > to) return false;
    if (!q) return true;
    return [l.name, l.email, l.phone, l.topic, l.message].some((v) => (v || '').toLowerCase().includes(q));
  });
};

const AdminDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [data, setData] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [filters, setFilters] = useState({ type: 'all', search: '', from: '', to: '' });

  useEffect(() => {
    const load = async () => {
      try {
        const u = user || (await getCurrentUser());
        setUser(u);
        if (!u.is_admin) {
          setForbidden(true);
          return;
        }
        setData(await getLeads());
      } catch (e) {
        if (e.message === 'forbidden') setForbidden(true);
        else navigate('/admin/login', { replace: true });
      }
    };
    load();
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = async () => {
    await logout();
    toast.success('Signed out');
    navigate('/admin/login', { replace: true });
  };

  const filtered = useMemo(() => (data ? filterLeads(data.leads, filters) : []), [data, filters]);
  const seo = <Seo title="Lead Dashboard | Kerry M Holloway Agency" description="Agency lead dashboard." path="/admin" noindex />;

  if (forbidden) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4" style={{ paddingTop: '200px', paddingBottom: '80px' }} data-testid="admin-forbidden">
        {seo}
        <div className="max-w-md text-center space-y-6 bg-white border border-gray-200 rounded-2xl p-10">
          <ShieldAlert className="h-10 w-10 text-[#D94B5A] mx-auto" />
          <h1 className="text-2xl font-bold text-[#1F1F1F]">Access restricted</h1>
          <p className="text-sm text-gray-600">{user?.email} is not authorized to view this dashboard.</p>
          <Button onClick={handleLogout} variant="outline" data-testid="forbidden-logout-button">Sign out</Button>
        </div>
      </div>
    );
  }

  if (!data) return <div className="min-h-screen bg-gray-50" data-testid="admin-loading">{seo}</div>;

  return (
    <div className="min-h-screen bg-gray-50" style={{ paddingTop: '220px', paddingBottom: '80px' }} data-testid="admin-dashboard">
      {seo}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-[#1F1F1F]">Lead Dashboard</h1>
            <p className="text-sm text-gray-600 mt-1" data-testid="admin-user-email">Signed in as {user.email}</p>
          </div>
          <Button onClick={handleLogout} variant="outline" className="border-gray-300" data-testid="logout-button">
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
        </div>
        <LeadStats total={data.total} quotes={data.quotes} contacts={data.contacts} />
        <LeadFilters filters={filters} onChange={setFilters} leads={filtered} />
        <LeadsTable leads={filtered} />
      </div>
    </div>
  );
};

export default AdminDashboard;
